import {
  applySnapshot,
  flow,
  getRoot,
  getSnapshot,
  types,
} from 'mobx-state-tree';
import find from 'lodash/find';
import { MessageSourceMST } from '../../constants';
import { RequestStateModel } from 'store/models/general-models';
import { IUserStore } from 'store/models/userStore';
import { FileModel } from 'store/models/files';
import { MessageDTO } from 'http-requests/messenger';
import { Messenger } from 'http-requests';
import { formatAttachmentsCount } from '../../utils';
import { MessageState } from 'components/message-state/message-state.types';
import { IReadInfoModel } from '../../types';

export const ReadInfoModel = types.model({
  id: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  full_name: types.optional(types.string, ''),
  read_at: types.optional(types.string, ''),
});

const SourceMessageModel = types
  .model('SourceMessageModel', {
    id: types.optional(types.maybeNull(types.string), null),
    content: types.optional(types.maybeNull(types.string), null),
    user_id: types.optional(types.maybeNull(types.string), null),
    user_number: types.optional(types.maybeNull(types.string), null),
    user_full_name: types.optional(types.maybeNull(types.string), null),
    user_provider_id: types.optional(types.maybeNull(types.string), null),
    user_provider_name: types.optional(types.maybeNull(types.string), null),
    user_position: types.optional(types.maybeNull(types.string), null),
    created_at: types.optional(types.maybeNull(types.string), null),
    conversation_id: types.optional(types.maybeNull(types.string), null),
    source: types.optional(types.maybeNull(MessageSourceMST), null),
    attached_files_ids: types.optional(types.array(types.string), []),
    is_removed: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get formatedAttachedFilesCount() {
      return formatAttachmentsCount(self.attached_files_ids.length);
    },
  }))
  .actions((self) => ({
    setMessageDelete(content: string) {
      self.content = content;
      self.is_removed = true;
    },
  }));

const MessageModelBase = types
  .model('MessageModel', {
    id: types.optional(types.identifier, ''),
    content: types.optional(types.string, ''),
    user_id: types.optional(types.string, ''),
    user_number: types.optional(types.maybeNull(types.string), null),
    user_full_name: types.optional(types.string, ''),
    user_provider_id: types.optional(types.string, ''),
    user_provider_name: types.optional(types.string, ''),
    user_position: types.optional(types.string, ''),
    created_at: types.optional(types.string, ''),
    conversation_id: types.optional(types.string, ''),
    source: types.optional(MessageSourceMST, 'system'),
    attached_files_ids: types.optional(types.array(types.string), []),
    src: types.optional(types.maybeNull(SourceMessageModel), {}),
    is_removed: types.optional(types.boolean, false),

    attachedFiles: types.optional(types.array(FileModel), []),
    read: types.optional(types.array(ReadInfoModel), []),
  })
  .volatile(() => ({
    createStatus: RequestStateModel.create(),
    getFilesInfoStatus: RequestStateModel.create(),
  }))
  .views((self) => ({
    get isOwn(): boolean {
      const user = getRoot<{ user: IUserStore }>(self).user;

      return self.user_number === user.auth.number;
    },
    /* Любое сообщение и его прочитали (вывод галочек) */
    get isRead(): boolean {
      return !!self.read.length;
    },
    /* Не моё сообщение и я его ещё не прочитал (отделение НОВЫХ для меня сообщений) */
    get isNew(): boolean {
      const user = getRoot<{ user: IUserStore }>(self).user;

      return (
        !this.isOwn &&
        !find(self.read, { number: user.auth.number }) &&
        self.source !== 'system'
      );
    },
    get messageState(): MessageState {
      return self.read.length ? 'read' : 'unread';
    },
    get allFilesAreLoaded() {
      return self.attachedFiles.every((file) => !file.getInfoStatus.pending);
    },
    get formatedAttachedFilesCount() {
      return formatAttachmentsCount(self.attached_files_ids.length);
    },
    get snapshot() {
      return getSnapshot(self);
    },
    hasUserReadInfo(number: string): boolean {
      return self.read.some((item) => item.number === number);
    },
  }))
  .actions((self) => ({
    setMessageDelete(content: string) {
      self.content = content;
      self.is_removed = true;
    },
    addReadInfo(data: IReadInfoModel) {
      self.read.replace([...self.read, data]);
    },
    updateSelf(updatedMessage: typeof self.snapshot) {
      applySnapshot(self, updatedMessage);
    },
    getFilesInfo: flow(function* () {
      try {
        const filesWithoutSharedIds = self.attachedFiles
          .filter((el) => !el.shared_id)
          .map((el) => el.id);
        const map = new Map<string, string>();

        if (filesWithoutSharedIds.length) {
          self.getFilesInfoStatus.loadingStates();

          const response = yield Messenger.GetFilesSharedIds(
            filesWithoutSharedIds
          );

          response.forEach((item: { file_id: string; shared_id: string }) => {
            map.set(item.file_id, item.shared_id);
          });
        }

        self.attachedFiles.forEach(async (file) => {
          const sharedId = map.get(file.id);

          if (sharedId) {
            file.updateSharedId(sharedId);
          }

          if (!file.filename) {
            await file.getInfo();
          }
        });

        self.getFilesInfoStatus.successStates();
      } catch (error) {
        self.getFilesInfoStatus.errorStates();
        console.error(error);
      } finally {
        self.getFilesInfoStatus.finallyStates();
      }
    }),
  }));

export const MessageModel = types.snapshotProcessor(MessageModelBase, {
  preProcessor(sn: Partial<MessageDTO>) {
    return {
      ...sn,
      attachedFiles:
        sn.attached_files_ids?.map((id) => FileModel.create({ id })) ?? [],
    };
  },
});
